import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, Table, TableHead, 
    TableRow, TableCell, TableBody, Paper, 
    CircularProgress, Alert, Stack, IconButton,
    useTheme, useMediaQuery, Chip, alpha
} from '@mui/material';
import {
    CloudUpload, Close,
    FileDownload, CheckCircle
} from '@mui/icons-material';
import Papa from 'papaparse';
import { toast } from 'react-toastify';
import api from '../utils/Api';

const BulkTeacherModal = ({ open, onClose, onSuccess }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [file, setFile] = useState(null);
    const [previewData, setPreviewData] = useState([]);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState(null);
    const [importErrors, setImportErrors] = useState([]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (!selectedFile) return;

        if (selectedFile.type !== "text/csv" && !selectedFile.name.endsWith('.csv')) {
            setError("Please upload a CSV file");
            return;
        }

        setFile(selectedFile);
        setError(null);

        Papa.parse(selectedFile, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                if (results.data && results.data.length > 0) {
                    const requiredHeaders = ['name', 'email', 'password'];
                    const headers = Object.keys(results.data[0]);
                    const missing = requiredHeaders.filter(h => !headers.includes(h));
                    
                    if (missing.length > 0) {
                        setError(`Missing required columns: ${missing.join(', ')}`);
                        setPreviewData([]);
                    } else {
                        setPreviewData(results.data.slice(0, 50)); // Preview first 50
                    }
                } else {
                    setError("No data found in file");
                }
            }
        });
    };

    const handleRemoveFile = () => {
        setFile(null);
        setPreviewData([]);
        setError(null);
        setImportErrors([]);
    };

    const downloadTemplate = () => {
        const headers = ["name", "email", "password", "gender", "phone", "bio"];
        const csvContent = headers.join(",") + "\nExample Teacher,teacher@example.com,p@ssword123,Male,0240000000,Experienced Math Teacher";
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = "teacher_import_template.csv";
        a.click();
        window.URL.revokeObjectURL(url);
    };

    const handleImport = async () => {
        if (!file) return;

        setImporting(true);
        setError(null);

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: async (results) => {
                try {
                    setImportErrors([]);
                    // Filter out completely empty objects
                    const validData = results.data.filter(row => row.name && row.name.trim() !== '');

                    const payload = {
                        teachers: validData
                    };

                    const res = await api.post('/teachers/bulk', payload);
                    const { summary, results: backendResults } = res.data;

                    if (summary.errorCount > 0) {
                        toast.warning(`Imported ${summary.successCount} teachers. ${summary.errorCount} failed.`);
                        if (backendResults && backendResults.errors && backendResults.errors.length > 0) {
                            setImportErrors(backendResults.errors);
                        }
                        if (summary.successCount > 0) onSuccess();
                    } else {
                        toast.success(`Successfully imported all ${summary.successCount} teachers.`);
                        onSuccess();
                        handleClose();
                    }
                } catch (err) {
                    console.error("Bulk import failed:", err);
                    setError(err.response?.data?.message || "Internal server error during import");
                } finally {
                    setImporting(false);
                }
            }
        });
    };

    const handleClose = () => {
        setFile(null);
        setPreviewData([]);
        setError(null);
        setImportErrors([]);
        onClose();
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="md" 
            fullWidth
            fullScreen={isMobile}
        >
            <DialogTitle sx={{ borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CloudUpload color="primary" />
                    <Typography variant="h6">Bulk Teacher Import</Typography>
                </Box>
                <IconButton onClick={handleClose} size="small"><Close /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
                <Stack spacing={3} sx={{ mt: 1 }}>
                    <Alert severity="info">
                        Use this feature to register many teachers at once. 
                        <strong> Important:</strong> Email addresses must be unique. Classes and subjects are skipped during bulk import.
                    </Alert>

                    <Box sx={{ p: 3, border: `2px dashed ${theme.palette.divider}`, borderRadius: 2, textAlign: 'center', bgcolor: alpha(theme.palette.background.default, 0.5) }}>
                        {!file ? (
                            <>
                                <input
                                    type="file"
                                    accept=".csv"
                                    id="bulk-teacher-file"
                                    style={{ display: 'none' }}
                                    onChange={handleFileChange}
                                />
                                <label htmlFor="bulk-teacher-file">
                                    <Button variant="contained" component="span" startIcon={<CloudUpload />}>
                                        Select CSV File
                                    </Button>
                                </label>
                                <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                                    Upload a CSV file containing teacher records
                                </Typography>
                            </>
                        ) : (
                            <Stack direction="row" alignItems="center" justifyContent="center" spacing={2}>
                                <Chip 
                                    icon={<CheckCircle />} 
                                    label={file.name} 
                                    color="success" 
                                    onDelete={handleRemoveFile} 
                                    variant="outlined"
                                />
                                <Typography variant="body2">{file.size} bytes</Typography>
                            </Stack>
                        )}
                    </Box>

                    {error && <Alert severity="error">{error}</Alert>}

                    {!file && (
                        <Button 
                            variant="text" 
                            startIcon={<FileDownload />} 
                            onClick={downloadTemplate}
                        >
                            Download CSV Template
                        </Button>
                    )}

                    {importErrors.length > 0 && (
                        <Box mt={1}>
                            <Alert severity="warning">Some rows failed to import. See details below:</Alert>
                            <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', mt: 1, p: 2 }}>
                                {importErrors.map((err, idx) => (
                                    <Typography key={idx} variant="body2" color="error" gutterBottom>
                                        <strong>{err.name ? err.name : (err.email ? err.email : `Row ${idx + 1}`)}:</strong> {err.message}
                                    </Typography>
                                ))}
                            </Paper>
                        </Box>
                    )}

                    {previewData.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                Preview (First {previewData.length} records)
                            </Typography>
                            <Paper variant="outlined" sx={{ maxHeight: 300, overflow: 'auto' }}>
                                <Table size="small" stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>NAME</TableCell>
                                            <TableCell>EMAIL</TableCell>
                                            <TableCell>GENDER</TableCell>
                                            <TableCell>PHONE</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {previewData.map((row, idx) => (
                                            <TableRow key={idx}>
                                                <TableCell>{row.name}</TableCell>
                                                <TableCell>{row.email}</TableCell>
                                                <TableCell>{row.gender || "-"}</TableCell>
                                                <TableCell>{row.phone || "-"}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Paper>
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <DialogActions sx={{ p: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Button onClick={handleClose} disabled={importing}>Cancel</Button>
                <Button 
                    onClick={handleImport} 
                    variant="contained" 
                    color="primary"
                    disabled={!file || importing || !!error}
                    startIcon={importing ? <CircularProgress size={20} /> : <CheckCircle />}
                >
                    {importing ? "Importing..." : "Start Import"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkTeacherModal;
